import React from "react";
import {
    ProductHeaderSerializer,
    ProductDescriptionSerializer,
    ProductMoreDescriptionsSerializer,
    BuyBuyBuyAreaSerializer,
} from "web/types/serializers";
import PriceRange from "web/react/components/buybuybuy-area/price-range/price-range";
import BuyOptions from "web/react/components/buybuybuy-area/buy-options/buy-options";
import ProductHeader from "web/react/components/buybuybuy-area/product-header/product-header";
import ProductDescription from "web/react/components/buybuybuy-area/product-description/product-description";

interface BuyAreaProps {
    csrfToken: string;
    header: ProductHeaderSerializer;
    buyAreaOptions: BuyBuyBuyAreaSerializer;
    productDescription: ProductDescriptionSerializer | null;
    productMoreDescriptions: ProductMoreDescriptionsSerializer | null;
    isOverlay?: boolean;
}

function BuyArea({
    header,
    csrfToken,
    buyAreaOptions,
    productDescription,
    productMoreDescriptions,
    isOverlay,
}: BuyAreaProps): React.ReactElement | null {
    const {
        min_and_max_price_are_the_same,
        multiple_stores,
        regular_price_with_currency_symbol,
        minimum_price_with_currency_symbol,
        size_guide_link,
        form_action,
        return_url,
        available_sizes,
        sizes,
        country,
        buybuybuy_options: { icon_options, affiliate_options, product_id, is_saved_for_later },
        user_default_schema,
        schema_labels,
    } = buyAreaOptions;

    const [regPriceWithCurSymbol, setRegPriceWithCurSymbol] = React.useState<string | null>(
        regular_price_with_currency_symbol
    );
    const [minPriceWithCurSymbol, setMinPriceWithCurSymbol] = React.useState<string | null>(
        minimum_price_with_currency_symbol
    );
    const [hasMultiplePricesPerSize, setHasMultiplePricesPerSize] = React.useState<boolean>(
        !min_and_max_price_are_the_same
    );
    const [hasMultipleStoresPerSize, setHasMultipleStoresPerSize] =
        React.useState<boolean>(multiple_stores);

    /*
        merging icon and affiliate buy options as they now have the same properties.
        We will split them again later but based on the size and availability but essentially they are all buy options.
        TODO maybe perform this merge on the back end. This may be something to consider in the flag cleanup ticket.
    */
    const allBuyOptions = [...icon_options, ...affiliate_options];

    return (
        <div className="buybuybuy-area">
            {header && <ProductHeader {...header} />}
            <PriceRange
                hasMultipleStoresPerSize={hasMultipleStoresPerSize}
                hasMultiplePricesPerSize={hasMultiplePricesPerSize}
                regularPriceWithCurrencySymbol={regPriceWithCurSymbol}
                minimumPriceWithCurrencySymbol={minPriceWithCurSymbol}
            />
            <BuyOptions
                isOverlay={isOverlay}
                buyOptions={allBuyOptions}
                productId={product_id}
                isSavedForLater={is_saved_for_later}
                setRegPriceWithCurSymbol={setRegPriceWithCurSymbol}
                setMinPriceWithCurSymbol={setMinPriceWithCurSymbol}
                setHasMultiplePricesPerSize={setHasMultiplePricesPerSize}
                setHasMultipleStoresPerSize={setHasMultipleStoresPerSize}
                sizeOptionsForm={{
                    csrfToken: csrfToken,
                    action: form_action,
                    country: country,
                    returnUrl: return_url,
                    availableSizes: available_sizes,
                    sizes: sizes,
                    sizeGuideLink: size_guide_link,
                    userDefaultSchema: user_default_schema,
                    schemas: schema_labels,
                }}
            />
            {productDescription && (
                <ProductDescription
                    {...productDescription}
                    more_descriptions={productMoreDescriptions}
                />
            )}
        </div>
    );
}

export default BuyArea;
